import { HeroControlRow } from '@wundo/ui';
import type { MeetingPageResponse } from '@wundo/shared/meetingPage.ts';
import type { MeetingTemplateItem } from '@wundo/shared/meetingTemplate.ts';
import {
  withCurrentTemplate,
  type MeetingTypeOption,
} from '@/lib/withCurrentTemplate.ts';
import type { MeetingDialog } from './meetingDialog.ts';
import { MeetingMoreControl } from './MeetingMoreControl.tsx';
import { MeetingNotesControl, type MeetingOfflineOffer } from './MeetingNotesControl.tsx';
import { meetingPageTitle } from './meetingPageTitle.ts';
import { MeetingSourcesControl } from './MeetingSourcesControl.tsx';
import { MeetingStaffControl } from './MeetingStaffControl.tsx';
import { MeetingTypeControl } from './MeetingTypeControl.tsx';
import { OfflineRecordingControl } from './OfflineRecordingControl.tsx';
import type { MeetingRunningRecording } from './useMeetingRecording.ts';
import type { MeetingWrites } from './useMeetingWrites.ts';

export interface MeetingSettingsRowProps {
  page: MeetingPageResponse;
  templates: MeetingTemplateItem[];
  /* Whether the organisation has the calendar integration, and so a notetaker. */
  calendarEnabled: boolean;
  /* Our own support menu, for a superadmin. */
  staff: boolean;
  correction: Pick<MeetingWrites['correction'], 'isPending' | 'reset'>;
  uploadDocument: Pick<
    MeetingWrites['uploadDocument'],
    'isPending' | 'isError' | 'mutate'
  >;
  recordingOffer: MeetingOfflineOffer;
  canRecord: boolean;
  recordingPending: boolean;
  runningRecording: MeetingRunningRecording | null;
  onStartRecording: () => void;
  onOpenRecording: () => void;
  onStopRecording: () => void;
  onResumeRecording: () => void;
  onOpenTypes: () => void;
  onPickTemplate: (template: MeetingTypeOption) => void;
  onOpenDialog: (dialog: MeetingDialog) => void;
}

/**
 * What this conversation is set to, side by side under the bar: its
 * gesprekstype, what Wundo does here, what Wundo reads along, and what is left
 * to do. Each says its own state; nothing here takes the user off the page.
 */
export function MeetingSettingsRow({
  page,
  templates,
  calendarEnabled,
  staff,
  correction,
  uploadDocument,
  recordingOffer,
  canRecord,
  recordingPending,
  runningRecording,
  onStartRecording,
  onOpenRecording,
  onStopRecording,
  onResumeRecording,
  onOpenTypes,
  onPickTemplate,
  onOpenDialog,
}: MeetingSettingsRowProps) {
  const manual = page.meeting.source === 'manual';
  const thread = page.thread;
  /* The menu offers this meeting's kind; one the user wrote down takes any. */
  const kindOptions = withCurrentTemplate(
    templates.filter(
      (template) => page.template_kind === null || template.kind === page.template_kind,
    ),
    page.template,
  );
  /*
   * Nothing to switch and nothing to record is nothing to open a panel for, and
   * without the calendar integration there is no notetaker to switch at all.
   */
  const notesWorthShowing =
    calendarEnabled && page.decision.follow && (!manual || recordingOffer !== null);

  return (
    <HeroControlRow className='mt-4.5'>
      <MeetingTypeControl
        current={page.template}
        templates={kindOptions}
        onOpen={onOpenTypes}
        onPick={onPickTemplate}
      />
      {notesWorthShowing && (
        <MeetingNotesControl
          on={page.decision.record}
          hasOnlineLink={Boolean(page.meeting.join_url)}
          manual={manual}
          offline={recordingOffer}
          canRecord={canRecord}
          pending={correction.isPending || recordingPending}
          onFlip={(record) => onOpenDialog({ kind: 'notetaker', record })}
          onStartRecording={onStartRecording}
        />
      )}
      {runningRecording && (
        <OfflineRecordingControl
          live={runningRecording.live}
          elapsed={runningRecording.elapsed}
          chunkLost={runningRecording.chunkLost}
          pending={recordingPending}
          onOpen={onOpenRecording}
          onStop={onStopRecording}
          onResume={onResumeRecording}
        />
      )}
      {/* On every conversation, however new: this is where documents are handed over. */}
      {thread && (
        <MeetingSourcesControl
          earlier={thread.past_meeting_count}
          previous={page.recap}
          name={meetingPageTitle(page)}
          documents={page.documents}
          uploading={uploadDocument.isPending}
          uploadFailed={uploadDocument.isError}
          onAddDocument={(file) => uploadDocument.mutate({ thread: thread.id, file })}
          onDeleteDocument={(document) =>
            onOpenDialog({ kind: 'delete-document', document })
          }
        />
      )}
      {/* There has to be something to take away before it can be offered. */}
      {!manual && page.decision.follow && (
        <MeetingMoreControl
          onRemove={() => {
            correction.reset();
            onOpenDialog({ kind: 'remove' });
          }}
        />
      )}
      {staff && (
        <MeetingStaffControl
          onTranscript={() => onOpenDialog({ kind: 'transcript' })}
          onTopics={() => onOpenDialog({ kind: 'topics' })}
        />
      )}
    </HeroControlRow>
  );
}
